"use client";
import { Button, Flex, Icon, IconButton, Text } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

export interface PaginateProps {
  pageCount: number;
  size?: "sm" | "md" | "lg";
  activePage?: number;
  onPageChange?: (page: number) => void;
}

const activeStyle = {
  bg: "#2A6FDB",
  color: "white",
  borderColor: "transparent",
  fontWeight: "600",
  _hover: { bg: "#1D5EC7", borderColor: "transparent" },
};

const inactiveStyle = {
  bg: "white",
  color: "#5B626E",
  borderColor: "#E0E3E9",
  fontWeight: "500",
  _hover: { bg: "#F5F6F8", borderColor: "#D1D5DC" },
};

const arrowStyle = {
  bg: "white",
  color: "#9AA0AC",
  borderColor: "#E0E3E9",
  _hover: { bg: "#F5F6F8", borderColor: "#D1D5DC" },
};

export function Paginate({
  pageCount,
  onPageChange,
  size = "md",
  activePage: activePageProp,
}: PaginateProps) {
  const maxPageButtons = 3;
  const activePage = useMemo(() => activePageProp || 1, [activePageProp]);

  const pageList: Array<string | number> = useMemo(() => {
    const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
    const activeIndex = pages.indexOf(activePage);

    if (pageCount <= maxPageButtons) {
      return pages.slice(1, pageCount);
    } else if (
      activeIndex === -1 ||
      activeIndex === 0 ||
      activeIndex <= maxPageButtons
    ) {
      const pageValues: Array<string | number> = pages.slice(
        1,
        pageCount > 5 ? 5 : pageCount - 1,
      );
      pageCount > 6 && pageValues.push("...");
      return pageValues;
    } else if (
      activeIndex >= maxPageButtons &&
      activeIndex >= pageCount - maxPageButtons
    ) {
      const pageValues: Array<string | number> = pages.slice(
        pageCount - maxPageButtons - 1,
        pageCount - 1,
      );
      pageValues.unshift("...");
      return pageValues;
    } else if (
      activeIndex >= maxPageButtons &&
      activeIndex <= pageCount - maxPageButtons
    ) {
      const pageValues: Array<string | number> = pages.slice(
        activeIndex - 2,
        activeIndex + 2,
      );
      pageValues.unshift("...");
      pageValues.push("...");
      return pageValues;
    }
    return [];
  }, [pageCount, activePage, maxPageButtons]);

  const sizeProps = useMemo(() => {
    switch (size) {
      case "sm":
        return { fontSize: "0.75rem", minW: "1.75rem", h: "1.75rem" };
      case "lg":
        return { fontSize: "1rem", minW: "2.5rem", h: "2.5rem" };
      default:
        return { fontSize: "0.8125rem", minW: "2rem", h: "2rem" };
    }
  }, [size]);

  const buttonProps = useMemo(
    () => ({
      borderRadius: "8px",
      size: "sm",
      ...sizeProps,
      variant: "outline",
      px: "2",
    }),
    [sizeProps],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      onPageChange && onPageChange(page);
    },
    [onPageChange],
  );

  return (
    <Flex data-testid="pagination" gap="6px" alignItems="center">
      <IconButton
        {...buttonProps}
        {...arrowStyle}
        isDisabled={activePage === 1}
        opacity={activePage === 1 ? 0.4 : 1}
        onClick={() => handlePageChange(activePage - 1)}
        aria-label="Previous Page"
        icon={<Icon fontSize="1rem" as={FiChevronLeft} />}
      />
      <Button
        {...buttonProps}
        {...(activePage === 1 ? activeStyle : inactiveStyle)}
        onClick={() => handlePageChange(1)}
      >
        {1}
      </Button>
      {pageList.map((page, index) =>
        typeof page === "string" ? (
          <Text
            color="#8B92A0"
            fontWeight="500"
            fontSize="0.8125rem"
            key={`pagination__text__${index}`}
            px="1"
          >
            {page}
          </Text>
        ) : (
          <Button
            data-testid={`pagination_button_${index + 1}`}
            key={`pagination__button__${index}`}
            {...buttonProps}
            {...(page === activePage ? activeStyle : inactiveStyle)}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </Button>
        ),
      )}

      {pageCount > maxPageButtons && (
        <Button
          {...buttonProps}
          {...(activePage === pageCount ? activeStyle : inactiveStyle)}
          onClick={() => handlePageChange(pageCount)}
        >
          {pageCount}
        </Button>
      )}
      <IconButton
        isDisabled={activePage === pageCount}
        {...buttonProps}
        {...arrowStyle}
        opacity={activePage === pageCount ? 0.4 : 1}
        onClick={() => handlePageChange(activePage + 1)}
        aria-label="Next Page"
        icon={<Icon fontSize="1rem" as={FiChevronRight} />}
      />
    </Flex>
  );
}
